import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Users, Mail, Euro, Pencil, Trash2, UserPlus, Check, X, Clock, Shield, ShieldOff, KeyRound } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OperaiPage() {
  const [workers, setWorkers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editWorker, setEditWorker] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', hourly_cost: '', prezzo_cliente: '' });
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'operaio' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [workersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/users?role=operaio`, { withCredentials: true }),
        axios.get(`${API}/users/pending`, { withCredentials: true })
      ]);
      setWorkers(workersRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const openEditDialog = (worker) => {
    setEditWorker(worker);
    setEditForm({
      name: worker.name || '',
      email: worker.email || '',
      password: '',
      hourly_cost: worker.hourly_cost || '',
      prezzo_cliente: worker.prezzo_cliente || ''
    });
    setEditDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: editForm.name,
        email: editForm.email,
        hourly_cost: parseFloat(editForm.hourly_cost) || 0,
        prezzo_cliente: parseFloat(editForm.prezzo_cliente) || 0
      };
      if (editForm.password) data.password = editForm.password;
      await axios.put(`${API}/users/${editWorker.id}`, data, { withCredentials: true });
      toast.success('Operaio aggiornato');
      setEditDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
  };

  const handleToggleStatus = async (worker) => {
    const newStatus = worker.status === 'attivo' ? 'sospeso' : 'attivo';
    try {
      await axios.put(`${API}/users/${worker.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`${worker.name} ${newStatus === 'attivo' ? 'abilitato' : 'disabilitato'}`);
      fetchAll();
    } catch (error) { toast.error('Errore'); }
  };

  const handleToggleMagazziniere = async (worker) => {
    const newFlag = !worker.is_magazziniere;
    try {
      await axios.put(`${API}/users/${worker.id}`, { is_magazziniere: newFlag }, { withCredentials: true });
      toast.success(`${worker.name}${newFlag ? ' abilitato come' : ' non è più'} magazziniere`);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (worker) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${worker.name}?`)) return;
    try {
      await axios.delete(`${API}/users/${worker.id}`, { withCredentials: true });
      toast.success(`${worker.name} eliminato`);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
  };

  const handleApprove = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}/approve`, {}, { withCredentials: true });
      toast.success(`${user.name} approvato`);
      fetchAll();
    } catch (error) { toast.error('Errore'); }
  };

  const handleReject = async (user) => {
    if (!window.confirm(`Rifiutare l'accesso a ${user.name}?`)) return;
    try {
      await axios.put(`${API}/users/${user.id}/reject`, {}, { withCredentials: true });
      toast.success(`${user.name} rifiutato`);
      fetchAll();
    } catch (error) { toast.error('Errore'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/admin-create-user`, { email: createForm.email, password: createForm.password, name: createForm.name, role: createForm.role || 'operaio' }, { withCredentials: true });
      toast.success(createForm.role === 'magazziniere' ? 'Magazziniere creato con successo' : 'Operaio creato con successo');
      setCreateDialogOpen(false);
      setCreateForm({ name: '', email: '', password: '', role: 'operaio' });
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore nella creazione'); }
  };

  const isAutoCreated = (w) => w && (w.email?.endsWith('@importato.local') || w.auto_created);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="operai-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Operai</h1>
          <p className="text-slate-500 mt-1">Gestisci operai, abilita accesso app e imposta costi</p>
        </div>
        <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCreateDialogOpen(true)} data-testid="create-worker-btn">
          <UserPlus className="h-4 w-4 mr-2" />Crea Operaio
        </Button>
      </div>

      {/* Pending Requests */}
      {pendingUsers.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800"><Clock className="h-5 w-5" />Richieste in Attesa ({pendingUsers.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-3">
              {pendingUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200" data-testid={`pending-user-${user.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-200 flex items-center justify-center"><span className="font-medium text-amber-800">{user.name?.charAt(0)}</span></div>
                    <div><p className="font-medium text-slate-900">{user.name}</p><p className="text-sm text-slate-500">{user.email}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 h-8" onClick={() => handleApprove(user)} data-testid={`approve-user-${user.id}`}><Check className="h-4 w-4 mr-1" />Approva</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => handleReject(user)} data-testid={`reject-user-${user.id}`}><X className="h-4 w-4 mr-1" />Rifiuta</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workers Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-slate-200">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Accesso App</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap" title="Abilita accesso completo al modulo Magazzino">Magazziniere</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Costo Orario</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Prezzo Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workers.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500"><Users className="h-12 w-12 mx-auto text-slate-300 mb-2" />Nessun operaio registrato</TableCell></TableRow>
              ) : workers.map((worker) => (
                <TableRow key={worker.id} className={`border-slate-100 hover:bg-slate-50 ${worker.status === 'sospeso' ? 'opacity-50' : ''}`} data-testid={`worker-row-${worker.id}`}>
                  <TableCell className="font-medium text-slate-900">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${worker.status === 'attivo' ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                        <span className="text-sm font-medium text-slate-600">{worker.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <span>{worker.name}</span>
                        {worker.role === 'magazziniere' && <Badge variant="outline" className="ml-2 text-[10px] text-purple-700 border-purple-200 bg-purple-50">Magazziniere</Badge>}
                        {isAutoCreated(worker) && <Badge variant="outline" className="ml-2 text-[10px] text-amber-600 border-amber-200 bg-amber-50">Importato</Badge>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Mail className="h-3 w-3" />
                      {isAutoCreated(worker) ? (
                        <span className="text-amber-600 text-xs">Da configurare</span>
                      ) : (
                        <span className="text-sm">{worker.email}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={worker.status === 'attivo'}
                        onCheckedChange={() => handleToggleStatus(worker)}
                        data-testid={`toggle-status-${worker.id}`}
                      />
                      {worker.status === 'attivo' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline"><Shield className="h-3 w-3 mr-1" />Abilitato</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200" variant="outline"><ShieldOff className="h-3 w-3 mr-1" />Disabilitato</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!(worker.is_magazziniere || worker.role === 'magazziniere')}
                        disabled={worker.role === 'magazziniere'}
                        onCheckedChange={() => handleToggleMagazziniere(worker)}
                        data-testid={`toggle-magazziniere-${worker.id}`}
                      />
                      {(worker.is_magazziniere || worker.role === 'magazziniere') ? (
                        <Badge className="bg-purple-100 text-purple-700 border-purple-200" variant="outline">Sì</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">No</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{worker.hourly_cost ? <span className="font-medium text-slate-900">{Number(worker.hourly_cost).toLocaleString('it-IT', {minimumFractionDigits: 2})} /h</span> : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell>{worker.prezzo_cliente ? <span className="font-medium text-blue-700">{Number(worker.prezzo_cliente).toLocaleString('it-IT', {minimumFractionDigits: 2})} /h</span> : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(worker)} className="h-8 w-8 text-slate-600 hover:text-slate-900" data-testid={`edit-worker-${worker.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(worker)} className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-worker-${worker.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog - Full */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>Modifica Operaio - {editWorker?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nome</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required data-testid="edit-worker-name" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Email (per accesso app)</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} required placeholder="operaio@email.com" data-testid="edit-worker-email" />
                {isAutoCreated(editWorker) && <p className="text-xs text-amber-600">Imposta un email valida per abilitare l'accesso all'app</p>}
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="flex items-center gap-2"><KeyRound className="h-4 w-4" />Password {editWorker && !isAutoCreated(editWorker) ? '(lascia vuoto per non cambiare)' : '(obbligatoria per accesso app)'}</Label>
                <Input type="password" value={editForm.password} onChange={(e) => setEditForm({...editForm, password: e.target.value})} placeholder="Nuova password" data-testid="edit-worker-password" />
              </div>
            </div>
            <div className="border-t pt-4 mt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Costi</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Costo Orario (azienda)</Label>
                  <div className="relative"><Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="number" step="0.01" min="0" value={editForm.hourly_cost} onChange={(e) => setEditForm({...editForm, hourly_cost: e.target.value})} placeholder="0.00" className="pl-10" data-testid="edit-hourly-cost-input" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Prezzo Cliente</Label>
                  <div className="relative"><Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input type="number" step="0.01" min="0" value={editForm.prezzo_cliente} onChange={(e) => setEditForm({...editForm, prezzo_cliente: e.target.value})} placeholder="0.00" className="pl-10" data-testid="edit-prezzo-cliente-input" /></div>
                </div>
              </div>
              {editForm.hourly_cost && editForm.prezzo_cliente && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 mt-3"><p className="text-sm text-emerald-700">Margine: <span className="font-bold">{(parseFloat(editForm.prezzo_cliente) - parseFloat(editForm.hourly_cost)).toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>/h</p></div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-worker-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Worker Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Crea Nuovo Operaio</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Nome</Label><Input value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} required placeholder="Mario Rossi" data-testid="create-worker-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} required placeholder="mario@email.com" data-testid="create-worker-email" /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={createForm.password} onChange={(e) => setCreateForm({...createForm, password: e.target.value})} required minLength={6} placeholder="Minimo 6 caratteri" data-testid="create-worker-password" /></div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white"
                data-testid="create-worker-role"
              >
                <option value="operaio">Operaio</option>
                <option value="magazziniere">Magazziniere (operaio + gestione magazzino)</option>
              </select>
              <p className="text-[11px] text-slate-500">Il magazziniere può fare tutto ciò che fa un operaio (rapportini, ferie, presenze) e in più gestisce il magazzino.</p>
            </div>
            <p className="text-xs text-slate-400">L'account sara attivo immediatamente</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-create-worker">Crea</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
